import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { adminGet, adminPatch } from "../../lib/adminApi";
import { PageHeader, ErrorBanner, OvergeslagenBanner } from "../../components/admin/AdminUI";
import { MfaSectie } from "../../components/admin/MfaSectie";

const CATEGORIE_LABEL = {
  algemeen: "Algemeen",
  kanaal_email: "Kanaal — E-mail (Resend)",
  kanaal_sms: "Kanaal — SMS (Twilio)",
  kanaal_whatsapp: "Kanaal — WhatsApp Business",
};

const INSTELLING_LABELS = {
  // Algemeen
  support_email: "Support e-mail",
  support_telefoon: "Support telefoon",
  // E-mail (Resend)
  kanaal_actief: "Kanaal actief",
  resend_api_key: "Resend API-sleutel",
  afzender_email: "Afzender e-mail",
  afzender_naam: "Afzender naam",
  // SMS (Twilio)
  twilio_account_sid: "Twilio Account SID",
  twilio_auth_token: "Twilio Auth Token",
  twilio_afzender_nummer: "Twilio afzendernummer",
  // WhatsApp
  wa_business_token: "WhatsApp Business Token",
  wa_business_phone_id: "WhatsApp Business telefoon-ID",
};

const CATEGORIE_OMSCHRIJVING = {
  algemeen: "Algemene app-instellingen die zichtbaar zijn voor gebruikers.",
  kanaal_email: "E-mail wordt verstuurd via Resend, vanaf het geverifieerde domein mail.fulkraworks.nl. De afzendernaam staat niet hier maar in Railway (RESEND_FROM_EMAIL).",
  kanaal_sms: "SMS is niet beschikbaar tot Twilio Account SID + Auth Token + afzendernummer zijn ingevuld. Account aanmaken op twilio.com.",
  kanaal_whatsapp: "WhatsApp is niet beschikbaar tot een Business Token en phone-id zijn ingevuld. Vereist Meta Business-account.",
};

export default function InstellingenPage() {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["instellingen"],
    queryFn: () => adminGet("/api/admin/instellingen"),
  });

  const items = q.data?.items || [];
  const groepen = useMemo(() => {
    const map = new Map();
    for (const it of items) {
      if (!map.has(it.categorie)) map.set(it.categorie, []);
      map.get(it.categorie).push(it);
    }
    return Array.from(map.entries());
  }, [items]);

  return (
    <div data-testid="instellingen-page">
      <PageHeader title="Instellingen" subtitle="Beheer kanaal-koppelingen en algemene app-instellingen" />
      <ErrorBanner error={q.error} />
      <OvergeslagenBanner
        aantal={q.data?.overgeslagen} enkelvoud="instelling" meervoud="instellingen"
        testid="instellingen-overgeslagen"
      />

      <div className="space-y-6">
        {/* Bovenaan: hier zet de beheerder tweestapsverificatie aan. Die sectie was op
            2026-05-31 per ongeluk uit deze pagina verdwenen terwijl de backend en het
            inlogscherm hem bleven verwachten — zie MfaSectie.jsx. */}
        <MfaSectie />
        <ToolsSectie />

        {q.isLoading ? (
          <div className="rounded-md border border-slate-200 bg-white p-8 text-center text-sm text-slate-400">Laden…</div>
        ) : (
          groepen.map(([categorie, instellingen]) => (
            <section key={categorie} className="rounded-md border border-slate-200 bg-white shadow-sm">
              <header className="border-b border-slate-200 bg-slate-50 px-4 py-3">
                <h2 className="text-sm font-semibold text-slate-900">{CATEGORIE_LABEL[categorie] || categorie}</h2>
                <p className="mt-0.5 text-xs text-slate-500">{CATEGORIE_OMSCHRIJVING[categorie] || ""}</p>
              </header>
              <ul className="divide-y divide-slate-100">
                {instellingen.map((it) => (
                  <InstellingRij key={it.instelling_id} it={it} onSaved={() => qc.invalidateQueries({ queryKey: ["instellingen"] })} />
                ))}
              </ul>
            </section>
          ))
        )}
      </div>
    </div>
  );
}

function ToolsSectie() {
  return (
    <section
      data-testid="instellingen-tools-sectie"
      className="rounded-md border border-slate-200 bg-white shadow-sm"
    >
      <header className="border-b border-slate-200 bg-slate-50 px-4 py-3">
        <h2 className="text-sm font-semibold text-slate-900">Tools</h2>
        <p className="mt-0.5 text-xs text-slate-500">Admin-hulpmiddelen</p>
      </header>
      <ul className="divide-y divide-slate-100">
        <li className="flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium text-slate-900">Vragenlijst preview</div>
            <p className="mt-0.5 text-xs text-slate-500">
              Open de huidige werknemer-vragenlijst in preview-modus. Antwoorden worden niet opgeslagen.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Link
              to="/admin/preview/vragenlijst"
              data-testid="btn-vragenlijst-preview"
              className="rounded-md border border-slate-300 bg-white px-3 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50"
            >
              Vragenlijst preview
            </Link>
          </div>
        </li>
      </ul>
    </section>
  );
}

function InstellingRij({ it, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [waarde, setWaarde] = useState(it.is_secret ? "" : (it.waarde || ""));

  const mut = useMutation({
    mutationFn: (body) => adminPatch(`/api/admin/instellingen/${it.categorie}/${it.sleutel}`, body),
    onSuccess: () => { toast.success("Opgeslagen"); setEditing(false); onSaved(); },
    onError: (e) => toast.error(e?.response?.data?.detail?.message || "Opslaan mislukt"),
  });

  return (
    <li className="flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium text-slate-900" data-testid={`instelling-titel-${it.sleutel}`}>{INSTELLING_LABELS[it.sleutel] ?? it.sleutel}</div>
        {it.omschrijving ? <p className="mt-0.5 text-xs text-slate-500">{it.omschrijving}</p> : null}
      </div>
      <div className="flex items-center gap-2">
        {!editing ? (
          <>
            <span className="font-mono text-xs text-slate-700">
              {it.is_ingevuld ? (it.is_secret ? it.waarde : it.waarde) : <em className="text-slate-400 not-italic">leeg</em>}
            </span>
            <button type="button" onClick={() => setEditing(true)} className="rounded-md border border-slate-300 bg-white px-3 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50">
              {it.is_ingevuld ? "Wijzigen" : "Invullen"}
            </button>
          </>
        ) : (
          <>
            <input
              type={it.is_secret ? "password" : "text"}
              value={waarde}
              onChange={(e) => setWaarde(e.target.value)}
              placeholder={it.is_secret ? "Plak nieuwe waarde" : (it.waarde || "")}
              className="w-72 rounded-md border border-slate-300 px-2 py-1 text-xs font-mono"
            />
            <button type="button" onClick={() => { setEditing(false); setWaarde(it.is_secret ? "" : (it.waarde || "")); }}
              className="rounded-md border border-slate-300 bg-white px-3 py-1 text-xs font-medium text-slate-700">Annuleren</button>
            <button type="button" disabled={mut.isPending}
              onClick={() => mut.mutate({ waarde: waarde || null })}
              className="rounded-md bg-blue-600 px-3 py-1 text-xs font-semibold text-white disabled:opacity-60">
              {mut.isPending ? "…" : "Opslaan"}
            </button>
          </>
        )}
      </div>
    </li>
  );
}
